import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  receptionsSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
  receptionSelector,
  receptionOrderSelector,
  receptionOrdersSelector,
  receptionContainersSelector,
} from './store/selector';
import { PutReceptionPayloadRequest } from './interfaces/put-reception';
import { receptionApiService } from './services/api';
import {
  getReceptionsInitApiAction,
  getReceptionsSuccessApiAction,
  getReceptionsFailureApiAction,
  postReceptionInitApiAction,
  postReceptionFailuireApiAction,
  putReceptionInitApiAction,
  putReceptionFailuireApiAction,
  deleteReceptionInitApiAction,
  deleteReceptionFailuireApiAction,
  setReceptionAction,
  increasePageAction,
  postReceptionSuccessApiAction,
  deleteReceptionSuccessApiAction,
  putReceptionSuccessApiAction,
  receptionClearAction,
  setReceptionOrderAction,
  getReceptionOrdersInitApiAction,
  getReceptionOrdersSuccessApiAction,
  getReceptionOrdersFailureApiAction,
  postReceptionOrderInitApiAction,
  postReceptionOrderFailuireApiAction,
  postReceptionOrderSuccessApiAction,
  putReceptionOrderInitApiAction,
  putReceptionOrderFailuireApiAction,
  putReceptionOrderSuccessApiAction,
  deleteReceptionOrderFailuireApiAction,
  deleteReceptionOrderSuccessApiAction,
  deleteReceptionOrderInitApiAction,
  getReceptionContainersFailureApiAction,
  getReceptionContainersSuccessApiAction,
  getReceptionContainersInitApiAction,
  putReceptionContainerInitApiAction,
  putReceptionContainerSuccessApiAction,
  putReceptionContainerFailuireApiAction,
  getReceptionContainerInitApiAction,
  getReceptionContainerSuccessApiAction,
  getReceptionContainerFailureApiAction,
} from './store/reducer';
import { PostReceptionPayloadRequest } from './interfaces/post-reception';
import { Reception } from '../../interfaces/reception.interface';
import { PostReceptionOrderPayloadRequest } from './interfaces/post-reception-order';
import { PutReceptionOrderPayloadRequest } from './interfaces/put-reception-order';
import { ReceptionOrder } from '../../interfaces/receptionOrder.interface';
import { PutReceptionContainerPayloadRequest } from './interfaces/put-reception-container';
import { ReceptionContainer } from '../../interfaces/receptionCotnainer.interface';

export function useReceptionCore() {
  const receptions = useSelector(receptionsSelector);
  const receptionOrders = useSelector(receptionOrdersSelector);
  const receptionContainers = useSelector(receptionContainersSelector);
  const reception = useSelector(receptionSelector);
  const receptionOrder = useSelector(receptionOrderSelector);
  const total = useSelector(totalSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getReceptions = useCallback(
    async (
      id: number,
      filters?: any,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getReceptionsInitApiAction());
        const response = await receptionApiService.getReceptions(id, filters);
        dispatch(getReceptionsSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getReceptionsFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getReceptionOrders = useCallback(
    async (
      id: number,
      filters?: any,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getReceptionOrdersInitApiAction());
        const response = await receptionApiService.getReceptionOrders(id, filters);
        console.log('useReceptionCore: Respuesta de la API getReceptionOrders:', response);
        console.log('useReceptionCore: Primera orden de ejemplo:', response.reception_orders[0]);
        if (response.reception_orders.length > 0) {
          console.log('useReceptionCore: Keys de la primera orden:', Object.keys(response.reception_orders[0]));
        }
        dispatch(getReceptionOrdersSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getReceptionOrdersFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getReceptionContainers = useCallback(
    async (
      cid: number,
      rid: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getReceptionContainersInitApiAction());
        const response = await receptionApiService.getReceptionContainers(cid, rid);
        dispatch(getReceptionContainersSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getReceptionContainersFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getReceptionContainer = useCallback(
    async (
      cId: number,
      rId: string,
      onSuccess?: (container: ReceptionContainer) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getReceptionContainerInitApiAction());
        const response = await receptionApiService.getReceptionContainer(cId, rId);
        dispatch(getReceptionContainerSuccessApiAction());
        onSuccess?.(response);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getReceptionContainerFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getAllReceptionContainers = useCallback(
    async (
      cId: number,
      page: number = 1,
      size: number = 100,
      onSuccess?: (containers: any[]) => void,
      onFailure?: () => void
    ) => {
      try {
        const response = await receptionApiService.getAllReceptionContainers(cId, page, size);
        onSuccess?.(response.origin_containers || []);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
      }
    },
    []
  );

  const postReception = useCallback(
    async (
      id: number,
      body: PostReceptionPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postReceptionInitApiAction());
        await receptionApiService.postReception(id, body);
        dispatch(postReceptionSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postReceptionFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postReceptionOrder = useCallback(
    async (
      id: number,
      body: PostReceptionOrderPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postReceptionOrderInitApiAction());
        await receptionApiService.postReceptionOrder(id, body);
        dispatch(postReceptionOrderSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postReceptionOrderFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putReception = useCallback(
    async (
      cId: number,
      pId: number,
      body: PutReceptionPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putReceptionInitApiAction());
        await receptionApiService.putReception(cId, pId, body);
        dispatch(putReceptionSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putReceptionFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putReceptionOrder = useCallback(
    async (
      cId: number,
      pId: number,
      body: PutReceptionOrderPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putReceptionOrderInitApiAction());
        await receptionApiService.putReceptionOrder(cId, pId, body);
        dispatch(putReceptionOrderSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putReceptionOrderFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const deleteReception = useCallback(
    async (cId: number, pId: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteReceptionInitApiAction());
        await receptionApiService.deleteReception(cId, pId);
        dispatch(deleteReceptionSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteReceptionFailuireApiAction());
      }
    },
    [dispatch]
  );

  const deleteReceptionOrder = useCallback(
    async (cId: number, pId: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteReceptionOrderInitApiAction());
        await receptionApiService.deleteReceptionOrder(cId, pId);
        dispatch(deleteReceptionOrderSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteReceptionOrderFailuireApiAction());
      }
    },
    [dispatch]
  );

  const putReceptionContainer = useCallback(
    async (
      cId: number,
      pId: number,
      rId: number,
      body: PutReceptionContainerPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putReceptionContainerInitApiAction());
        await receptionApiService.putReceptionContainer(cId, pId, rId, body);
        dispatch(putReceptionContainerSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putReceptionContainerFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const setReception = useCallback(
    (reception: Reception) => {
      dispatch(setReceptionAction(reception));
    },
    [dispatch]
  );

  const setReceptionOrder = useCallback(
    (reception: ReceptionOrder) => {
      dispatch(setReceptionOrderAction(reception));
    },
    [dispatch]
  );

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearReception = useCallback(() => {
    dispatch(receptionClearAction());
  }, [dispatch]);

  return {
    receptions,
    receptionOrders,
    receptionContainers,
    reception,
    receptionOrder,
    total,
    page,
    noMore,
    errorCode,
    getReceptions,
    getReceptionContainers,
    getReceptionOrders,
    postReception,
    postReceptionOrder,
    putReception,
    putReceptionOrder,
    deleteReception,
    deleteReceptionOrder,
    getReceptionContainer,
    getAllReceptionContainers,
    putReceptionContainer,
    setReception,
    setReceptionOrder,
    increasePage,
    clearReception,
  };
}
